// The practitioner's review console (`admin/reviews/`).
//
// Its vocabulary is deliberately narrower than the operator's: the practice can ANSWER once
// and ASK for a look once, and nothing here may suggest it can remove a review — that is the
// platform's decision, and a label that implied otherwise would promise a power the API
// refuses.
//
// `rev.error.<CODE>` mirrors `REVIEW_ERROR_CODES` member for member in this namespace too:
// the same refusal reaches the practitioner through `reviewErrorText(e, {prefix: "rev"})`,
// and it is said to them, not to their client.
export default {
  fr: {
    "nav.reviews": "Avis clients",

    "rev.title": "Avis clients",
    "rev.subtitle": "Ce que vos clients disent de vos séances, publié sur votre profil du marketplace.",
    "rev.loading": "Chargement…",

    "rev.summary.aria": "{avg} sur 5 étoiles",
    "rev.summary.none": "Pas encore d'avis",
    "rev.summary.count": "{count} avis",

    "rev.list.title": "Tous les avis",
    "rev.list.filterFlagged": "Signalés uniquement",
    "rev.list.empty": "Aucun avis pour le moment.",

    "rev.row.aria": "{avg} sur 5 étoiles",
    "rev.row.hidden": "Masqué par la plateforme",
    "rev.row.flagged": "Signalé",

    "rev.reply.yours": "Votre réponse",
    "rev.reply.label": "Répondre publiquement (une seule fois)",
    "rev.reply.placeholder": "Votre réponse sera visible par tous sur votre profil…",
    "rev.reply.submit": "Publier la réponse",
    "rev.reply.submitting": "Publication…",
    "rev.reply.saved": "Votre réponse est publiée.",

    "rev.flag.button": "Signaler cet avis",
    "rev.flag.title": "Signaler cet avis",
    "rev.flag.desc": "Un membre de la plateforme lira votre signalement. L'avis reste visible tant qu'aucune décision n'est prise, et vous ne pouvez le signaler qu'une fois.",
    "rev.flag.placeholder": "Expliquez pourquoi cet avis vous semble problématique…",
    "rev.flag.submit": "Envoyer le signalement",
    "rev.flag.submitting": "Envoi…",
    "rev.flag.saved": "Signalement envoyé.",

    "rev.page.info": "{from}–{to} sur {total}",
    "rev.page.prev": "Précédent",
    "rev.page.next": "Suivant",

    "rev.error.REVIEWS_DISABLED": "Le programme d'avis est suspendu : cette action est indisponible pour le moment.",
    "rev.error.REVIEW_TOKEN_UNKNOWN": "Ce lien d'avis n'est pas valide.",
    "rev.error.REVIEW_TOKEN_EXPIRED": "Ce lien d'avis a expiré.",
    "rev.error.REVIEW_ALREADY_SUBMITTED": "Un avis a déjà été déposé pour ce rendez-vous.",
    "rev.error.REVIEW_INVALID": "Ce texte n'est pas valide : vérifiez qu'il n'est ni vide ni trop long.",
    "rev.error.REVIEW_NOT_FOUND": "Cet avis est introuvable.",
    "rev.error.REVIEW_ALREADY_REPLIED": "Vous avez déjà répondu à cet avis : une réponse est définitive.",
    "rev.error.REVIEW_ALREADY_FLAGGED": "Vous avez déjà signalé cet avis.",
    "rev.error.REVIEW_ALREADY_HIDDEN": "Cet avis est déjà masqué.",
    "rev.error.REVIEW_NOT_HIDDEN": "Cet avis n'est pas masqué.",
    "rev.error.REVIEW_PROGRAM_INVALID": "Ces réglages du programme d'avis ne sont pas valides.",
    "rev.error.PRACTITIONER_NOT_FOUND": "Ce cabinet est introuvable sur le marketplace.",
    "rev.error.RATE_LIMITED": "Trop de requêtes : réessayez dans un instant.",
    "rev.error.UNKNOWN": "Une erreur est survenue et nous ne pouvons pas en dire plus.",
  },
  en: {
    "nav.reviews": "Client reviews",

    "rev.title": "Client reviews",
    "rev.subtitle": "What your clients say about your sessions, published on your marketplace profile.",
    "rev.loading": "Loading…",

    "rev.summary.aria": "{avg} out of 5 stars",
    "rev.summary.none": "No reviews yet",
    "rev.summary.count": "{count} reviews",

    "rev.list.title": "All reviews",
    "rev.list.filterFlagged": "Flagged only",
    "rev.list.empty": "No reviews yet.",

    "rev.row.aria": "{avg} out of 5 stars",
    "rev.row.hidden": "Hidden by the platform",
    "rev.row.flagged": "Flagged",

    "rev.reply.yours": "Your reply",
    "rev.reply.label": "Reply publicly (once only)",
    "rev.reply.placeholder": "Your reply will be visible to everyone on your profile…",
    "rev.reply.submit": "Publish reply",
    "rev.reply.submitting": "Publishing…",
    "rev.reply.saved": "Your reply is published.",

    "rev.flag.button": "Flag this review",
    "rev.flag.title": "Flag this review",
    "rev.flag.desc": "Someone at the platform will read your report. The review stays visible until a decision is made, and you can only flag it once.",
    "rev.flag.placeholder": "Explain why this review looks like a problem…",
    "rev.flag.submit": "Send the report",
    "rev.flag.submitting": "Sending…",
    "rev.flag.saved": "Report sent.",

    "rev.page.info": "{from}–{to} of {total}",
    "rev.page.prev": "Previous",
    "rev.page.next": "Next",

    "rev.error.REVIEWS_DISABLED": "The review program is paused: this action is unavailable for now.",
    "rev.error.REVIEW_TOKEN_UNKNOWN": "This review link is not valid.",
    "rev.error.REVIEW_TOKEN_EXPIRED": "This review link has expired.",
    "rev.error.REVIEW_ALREADY_SUBMITTED": "A review has already been left for this appointment.",
    "rev.error.REVIEW_INVALID": "This text is not valid: check that it is neither empty nor too long.",
    "rev.error.REVIEW_NOT_FOUND": "This review no longer exists.",
    "rev.error.REVIEW_ALREADY_REPLIED": "You have already replied to this review — a reply is final.",
    "rev.error.REVIEW_ALREADY_FLAGGED": "You have already flagged this review.",
    "rev.error.REVIEW_ALREADY_HIDDEN": "This review is already hidden.",
    "rev.error.REVIEW_NOT_HIDDEN": "This review is not hidden.",
    "rev.error.REVIEW_PROGRAM_INVALID": "These review-program settings are not valid.",
    "rev.error.PRACTITIONER_NOT_FOUND": "This practice cannot be found on the marketplace.",
    "rev.error.RATE_LIMITED": "Too many requests — try again in a moment.",
    "rev.error.UNKNOWN": "Something went wrong and we cannot say what.",
  },
  es: {
    "nav.reviews": "Opiniones de clientes",

    "rev.title": "Opiniones de clientes",
    "rev.subtitle": "Lo que sus clientes dicen de sus sesiones, publicado en su perfil del marketplace.",
    "rev.loading": "Cargando…",

    "rev.summary.aria": "{avg} de 5 estrellas",
    "rev.summary.none": "Aún no hay opiniones",
    "rev.summary.count": "{count} opiniones",

    "rev.list.title": "Todas las opiniones",
    "rev.list.filterFlagged": "Solo señaladas",
    "rev.list.empty": "Todavía no hay opiniones.",

    "rev.row.aria": "{avg} de 5 estrellas",
    "rev.row.hidden": "Ocultada por la plataforma",
    "rev.row.flagged": "Señalada",

    "rev.reply.yours": "Su respuesta",
    "rev.reply.label": "Responder públicamente (una sola vez)",
    "rev.reply.placeholder": "Su respuesta será visible para todos en su perfil…",
    "rev.reply.submit": "Publicar la respuesta",
    "rev.reply.submitting": "Publicando…",
    "rev.reply.saved": "Su respuesta está publicada.",

    "rev.flag.button": "Señalar esta opinión",
    "rev.flag.title": "Señalar esta opinión",
    "rev.flag.desc": "Alguien de la plataforma leerá su aviso. La opinión sigue visible hasta que se tome una decisión, y solo puede señalarla una vez.",
    "rev.flag.placeholder": "Explique por qué esta opinión le parece problemática…",
    "rev.flag.submit": "Enviar el aviso",
    "rev.flag.submitting": "Enviando…",
    "rev.flag.saved": "Aviso enviado.",

    "rev.page.info": "{from}–{to} de {total}",
    "rev.page.prev": "Anterior",
    "rev.page.next": "Siguiente",

    "rev.error.REVIEWS_DISABLED": "El programa de opiniones está en pausa: esta acción no está disponible por ahora.",
    "rev.error.REVIEW_TOKEN_UNKNOWN": "Este enlace de opinión no es válido.",
    "rev.error.REVIEW_TOKEN_EXPIRED": "Este enlace de opinión ha caducado.",
    "rev.error.REVIEW_ALREADY_SUBMITTED": "Ya se ha dejado una opinión para esta cita.",
    "rev.error.REVIEW_INVALID": "Este texto no es válido: compruebe que no esté vacío ni sea demasiado largo.",
    "rev.error.REVIEW_NOT_FOUND": "Esta opinión ya no existe.",
    "rev.error.REVIEW_ALREADY_REPLIED": "Ya ha respondido a esta opinión: una respuesta es definitiva.",
    "rev.error.REVIEW_ALREADY_FLAGGED": "Ya ha señalado esta opinión.",
    "rev.error.REVIEW_ALREADY_HIDDEN": "Esta opinión ya está oculta.",
    "rev.error.REVIEW_NOT_HIDDEN": "Esta opinión no está oculta.",
    "rev.error.REVIEW_PROGRAM_INVALID": "Estos ajustes del programa de opiniones no son válidos.",
    "rev.error.PRACTITIONER_NOT_FOUND": "No se encuentra este consultorio en el marketplace.",
    "rev.error.RATE_LIMITED": "Demasiadas solicitudes: vuelva a probar en un momento.",
    "rev.error.UNKNOWN": "Ha ocurrido un error y no podemos decir más.",
  },
};
